import { ICellRendererParams } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FontSize } from '../../../../../styles/commonStyles'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Tooltip from '@mui/material/Tooltip'
import { inputErrorColor } from '../../../commons/AgGrid/lib/validator'
import LanguageIcon from '../../../../components/icons/LanguageIcon'
import { styled } from '@mui/material'
import { setError } from '../../lib/validation'
import { useIntl } from 'react-intl'

interface Props extends ICellRendererParams {
  validate?: (value: any, params: ICellRendererParams) => string | undefined
}

export const START_EDITING_I18N_LABELS = 'START_EDITING_I18N_LABELS'
export const WbsItemAdditionalPropertyNameCellRenderer = (props: Props) => {
  const { value, data, node, validate } = props
  const intl = useIntl()
  const ref = useRef<HTMLDivElement | null>(null)
  const [openTooltip, setOpenTooltip] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  useEffect(() => {
    if (!validate) return
    const err = validate(value, props)
    setError(data, err, props)
    setErrorMessage(err)
  }, [value])

  const startEditingI18n = useCallback(() => {
    if (!props.column) return
    props.api.startEditingCell({
      rowIndex: props.node?.rowIndex ?? 0,
      colKey: props.column,
      key: START_EDITING_I18N_LABELS,
    })
  }, [props.api, props.node?.rowIndex, props.column])

  const i18nTooltip = useMemo(
    () =>
      intl.formatMessage({
        id: 'multipleLanguages',
      }),
    [intl]
  )

  return (
    <ClickAwayListener onClickAway={() => setOpenTooltip(false)}>
      <StyledTooltip message={errorMessage} open={openTooltip}>
        <RootDiv
          id={`tree-column-${props.node?.rowIndex}`}
          ref={ref}
          onClick={e => setOpenTooltip(!openTooltip)}
          onMouseLeave={e => setOpenTooltip(false)}
        >
          <Name
            style={{
              backgroundColor: value ? 'none' : inputErrorColor,
            }}
          >
            {value}
          </Name>
          <StyledTooltip message={i18nTooltip}>
            <StyledLanguageIcon color="action" onClick={startEditingI18n} />
          </StyledTooltip>
        </RootDiv>
      </StyledTooltip>
    </ClickAwayListener>
  )
}

const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  padding: '0 5px 0 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  marginLeft: '3px',
  fontWeight: 'medium',
})
const Name = styled('div')({
  fontWeight: 'normal !important',
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  marginLeft: '5px',
})
const StyledLanguageIcon = styled(LanguageIcon)({
  cursor: 'pointer',
})
const StyledTooltip = ({
  message,
  children,
  open,
}: {
  message?: string
  children: React.ReactElement
  open?: boolean
}) => {
  return (
    <Tooltip
      title={message}
      placement={'bottom-start'}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: `${FontSize.MEDIUM}px`,
            marginTop: '5px !important',
          },
        },
      }}
      open={open}
    >
      {children}
    </Tooltip>
  )
}
